import React, { useMemo } from 'react';
import { Box, Typography, Button, CircularProgress, Tooltip } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { ConnectionQuality } from 'livekit-client';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import SignalCellularAlt2BarIcon from '@mui/icons-material/SignalCellularAlt2Bar';
import SignalCellularAlt1BarIcon from '@mui/icons-material/SignalCellularAlt1Bar';
import SignalCellularConnectedNoInternet0BarIcon from '@mui/icons-material/SignalCellularConnectedNoInternet0Bar';
import { useTranslation } from 'react-i18next';
import { useOutboundCallStatusStyles } from './OutboundCallStatus.styles';
import { TestingConversationView } from '../TestingConversationView';
import { useTestingConversationMessages } from '../TestingConversationView/useTestingConversationMessages';
import { useConnectionQuality } from '../../hooks';
import { CopyCallIdButton } from '@shared/components/CopyCallIdButton';

const getConnectionQualityConfig = (
  theme: Theme,
): Record<
  ConnectionQuality,
  { icon: typeof SignalCellularAltIcon; color: string; labelKey: string }
> => ({
  [ConnectionQuality.Excellent]: {
    icon: SignalCellularAltIcon,
    color: theme.palette.success.main,
    labelKey: 'agentPage.outboundCall.connectionQuality.excellent',
  },
  [ConnectionQuality.Good]: {
    icon: SignalCellularAlt2BarIcon,
    color: theme.palette.success.light,
    labelKey: 'agentPage.outboundCall.connectionQuality.good',
  },
  [ConnectionQuality.Poor]: {
    icon: SignalCellularAlt1BarIcon,
    color: theme.palette.warning.main,
    labelKey: 'agentPage.outboundCall.connectionQuality.poor',
  },
  [ConnectionQuality.Lost]: {
    icon: SignalCellularConnectedNoInternet0BarIcon,
    color: theme.palette.error.main,
    labelKey: 'agentPage.outboundCall.connectionQuality.lost',
  },
  [ConnectionQuality.Unknown]: {
    icon: SignalCellularAlt1BarIcon,
    color: theme.palette.text.secondary,
    labelKey: 'agentPage.outboundCall.connectionQuality.unknown',
  },
});

interface OutboundCallStatusProps {
  phoneNumber: string;
  onClose: () => void;
  agentName?: string;
  callId?: string | null;
  sipParticipantState: {
    connected: boolean;
    answered: boolean;
  };
}

export const OutboundCallStatus = ({
  phoneNumber,
  onClose,
  agentName = 'Agent',
  callId,
  sipParticipantState,
}: OutboundCallStatusProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { classes } = useOutboundCallStatusStyles();
  const connectionQualityConfig = useMemo(() => getConnectionQualityConfig(theme), [theme]);
  const connectionQuality = useConnectionQuality();
  const conversationMessages = useTestingConversationMessages();
  const hasConversationMessages = conversationMessages.length > 0;
  const isWaitingForFirstMessage = sipParticipantState.answered && !hasConversationMessages;

  const getStatusText = () => {
    if (!sipParticipantState.connected) {
      return t('agentPage.outboundCall.status.initiating');
    }
    if (!sipParticipantState.answered) {
      return t('agentPage.outboundCall.status.waitingForAnswer');
    }
    if (isWaitingForFirstMessage) {
      return t('campaignPage.conversationNotStartedYet');
    }
    return t('agentPage.outboundCall.status.inProgress');
  };

  const qualityConfig = connectionQualityConfig[connectionQuality];
  const QualityIcon = qualityConfig.icon;
  const showConversationLayout = sipParticipantState.answered && hasConversationMessages;
  const isWaiting = !sipParticipantState.connected || !showConversationLayout;

  return (
    <Box className={classes.container}>
      {!showConversationLayout ? (
        <Box className={classes.statusSection}>
          <Box className={classes.iconWrapper}>
            <PhoneInTalkIcon className={classes.callIcon} />
          </Box>
          <Typography className={classes.title}>{t('agentPage.outboundCall.title')}</Typography>
          <Typography className={classes.phoneNumber}>{phoneNumber}</Typography>
          <Box className={classes.statusRow}>
            {isWaiting && <CircularProgress size={16} thickness={5} />}
            <Typography className={classes.statusText}>{getStatusText()}</Typography>
          </Box>
          {sipParticipantState.connected && (
            <Tooltip title={t(qualityConfig.labelKey)} arrow>
              <Box className={classes.qualityIndicator}>
                <QualityIcon sx={{ color: qualityConfig.color, fontSize: 16 }} />
                <Typography className={classes.qualityLabel} sx={{ color: qualityConfig.color }}>
                  {t(qualityConfig.labelKey)}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
      ) : (
        <Box className={classes.conversationHeader}>
          <Box className={classes.conversationHeaderLeft}>
            <Box className={classes.liveIndicator}>
              <Box className={classes.liveDot} />
              <Typography className={classes.liveText}>
                {t('agentPage.outboundCall.status.inProgress')}
              </Typography>
            </Box>
            <Typography className={classes.headerPhoneNumber}>{phoneNumber}</Typography>
            <CopyCallIdButton callId={callId} showLabel />
          </Box>
          <Tooltip title={t(qualityConfig.labelKey)} arrow>
            <Box className={classes.qualityIndicator}>
              <QualityIcon sx={{ color: qualityConfig.color, fontSize: 16 }} />
              <Typography className={classes.qualityLabel} sx={{ color: qualityConfig.color }}>
                {t(qualityConfig.labelKey)}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      )}

      {showConversationLayout && (
        <Box className={classes.conversationSection}>
          <TestingConversationView agentName={agentName} messages={conversationMessages} />
        </Box>
      )}

      <Box className={classes.buttonContainer}>
        <Button
          variant="contained"
          color="primary"
          onClick={onClose}
          className={classes.closeButton}>
          {t('agentPage.outboundCall.buttons.close')}
        </Button>
      </Box>
    </Box>
  );
};
